import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Linkedin, Building2, Pencil, Plus, StickyNote, Star, Smartphone } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { CommittenteBadge } from "../components/CommittenteSelect";
import Timeline from "../components/Timeline";
import FormDialog from "../components/FormDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_FIELDS, ACTIVITY_FIELDS } from "../lib/entities";
import { fmtDateTime, fmtDate, initials, label } from "../lib/format";

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => { const { data } = await api.get(`/contacts/${id}`); setC(data); }, [id]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/notes", { contact_id: id, company_id: c.company_id, content: note });
    setNote(""); toast.success("Nota aggiunta"); load();
  };

  if (!c) return <div className="text-slate-400 py-10">Caricamento…</div>;

  return (
    <div>
      <button onClick={() => nav("/contatti")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Contatti</button>
      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 mb-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center font-heading font-bold text-lg">{initials(`${c.first_name} ${c.last_name}`)}</div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <h1 className="text-xl sm:text-2xl font-bold font-heading text-slate-900 break-words">{c.first_name} {c.last_name}</h1>
                {c.decision_maker && <Star className="w-5 h-5 text-amber-500 fill-amber-500" />}
                <StatusBadge group="contact_status" value={c.relationship_status} />
                {c.contact_type && <StatusBadge group="contact_type" value={c.contact_type} />}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{c.job_title || "—"} {c.company && <>· <button onClick={() => nav(`/aziende/${c.company_id}`)} className="text-teal-600 hover:underline">{c.company.legal_name}</button></>}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {can("write") && <Button data-testid="contact-add-activity" onClick={() => setDialog({ title: "Nuova Attività", fields: ACTIVITY_FIELDS, endpoint: "/activities" })} className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5"><Plus className="w-4 h-4" /> Attività</Button>}
            {can("write") && <Button variant="outline" data-testid="edit-contact-detail" onClick={() => setEdit(true)} className="gap-1.5"><Pencil className="w-4 h-4" /> Modifica</Button>}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-slate-100 text-sm min-w-0 [&>*]:min-w-0 [&>*]:truncate">
          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-teal-600 hover:underline"><Mail className="w-4 h-4 shrink-0" />{c.email}</a>}
          {c.phone && <span className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 shrink-0" />{c.phone}</span>}
          {c.mobile && <span className="flex items-center gap-2 text-slate-600"><Smartphone className="w-4 h-4 shrink-0" />{c.mobile}</span>}
          {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-teal-600 hover:underline"><Linkedin className="w-4 h-4 shrink-0" />LinkedIn</a>}
          {c.sales_navigator_url && <a data-testid="contact-sales-navigator-link" href={c.sales_navigator_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-teal-600 hover:underline"><Linkedin className="w-4 h-4 shrink-0" />Sales Navigator</a>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3 text-sm">
          {[["Tipologia", c.contact_type ? label("contact_type", c.contact_type) : null], ["Committente", c.committente ? <CommittenteBadge value={c.committente} testid="contact-detail-committente" /> : null], ["Seniority", c.seniority], ["Dipartimento", c.department], ["Città", c.city], ["Regione", c.region], ["Paese", c.country], ["Interazioni", c.interactions], ["Prossimo follow-up", c.next_followup ? fmtDate(c.next_followup) : null]].map(([k, v]) => (
            <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="text-slate-700">{v ?? "—"}</p></div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="attivita">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto justify-start">
          <TabsTrigger value="attivita" data-testid="entity-tab-attivita">Attività</TabsTrigger>
          <TabsTrigger value="note" data-testid="entity-tab-note">Note</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="entity-tab-timeline">Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="attivita" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {c.activities.length ? c.activities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
                <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{a.subject}</p><p className="text-xs text-slate-400">{label("activity_type", a.type)} · {fmtDateTime(a.due_date)}</p></div>
                <StatusBadge group="activity_status" value={a.status} />
              </div>
            )) : <p className="text-sm text-slate-400 text-center py-10">Nessuna attività</p>}
          </div>
        </TabsContent>
        <TabsContent value="note" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            {can("write") && <div className="flex flex-col sm:flex-row gap-2 mb-4"><Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota…" rows={2} /><Button data-testid="add-note-btn" onClick={addNote} className="bg-navy-900 hover:bg-navy-700 text-white sm:self-end min-h-[44px] sm:min-h-0">Aggiungi</Button></div>}
            <div className="space-y-2">
              {(c.notes_list || []).map((n) => <div key={n.id} className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50"><StickyNote className="w-4 h-4 text-slate-400 mt-0.5" /><div className="flex-1"><p className="text-sm text-slate-700">{n.content}</p><p className="text-xs text-slate-400 mt-1 font-mono">{fmtDateTime(n.created_at)}</p></div></div>)}
              {(c.notes_list || []).length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nessuna nota</p>}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="timeline" className="mt-4"><div className="bg-white rounded-xl border border-slate-200 p-5"><Timeline events={c.timeline} /></div></TabsContent>
      </Tabs>

      {edit && <FormDialog open={edit} onOpenChange={setEdit} title="Modifica Contatto" fields={CONTACT_FIELDS} initial={c} endpoint={`/contacts/${id}`} method="put" onSaved={load} />}
      {dialog && <FormDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} title={dialog.title} fields={dialog.fields} endpoint={dialog.endpoint} initial={{ contact_id: id, company_id: c.company_id }} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
